'use client';

import { useMemo } from 'react';
import { useCategoriesQuery, useLanguagesQuery, useRegionsQuery } from '~/features/activities/ActivityFilterBar/config/shared';
import { useI18n } from '~/features/i18n';
import { useLocalStorageState } from '~/modules/browser/storage';
import { useActivitySearchCtx } from './context';
import { CACHED_REGION_CITY_SEARCH } from './context';
import { buildAiFilterChips, type AiFilterChip } from './ai-filter-chips';
import { isNearPointActive } from './nearPoint';
import { DEFAULT_RADIUS_KM } from '~/features/activities/ActivityFilterBar/config/region.activityFilters';
type CategoryLike = {
  id?: string | number | null;
  name?: string | null;
  children?: ReadonlyArray<CategoryLike | null> | null;
};
function collectCategoryNames(categories: ReadonlyArray<CategoryLike | null> | undefined, into: Map<number, string>): void {
  if (!categories) return;
  for (const category of categories) {
    if (!category) continue;
    if (category.id != null && category.name) into.set(Number(category.id), category.name);
    if (category.children) collectCategoryNames(category.children, into);
  }
}

/** Active-filter chips for the AI composer, with display names resolved from queries. */
export function useActivityAiFilterChips(): AiFilterChip[] {
  const i18n = useI18n();
  const filters = useActivitySearchCtx().useFilters();
  const regionsQuery = useRegionsQuery();
  const categoriesQuery = useCategoriesQuery();
  const languagesQuery = useLanguagesQuery();
  const cityCache = useLocalStorageState(CACHED_REGION_CITY_SEARCH);
  const regionNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const region of regionsQuery.regions ?? []) {
      if (region.id != null && region.name) map.set(Number(region.id), region.name);
    }
    return map;
  }, [regionsQuery.regions]);
  const categoryNameById = useMemo(() => {
    const map_0 = new Map<number, string>();
    collectCategoryNames(categoriesQuery.categories, map_0);
    return map_0;
  }, [categoriesQuery.categories]);
  const languageNameById = useMemo(() => {
    const map_1 = new Map<number, string>();
    for (const language of languagesQuery.languages ?? []) {
      if (language.id != null && language.name) map_1.set(Number(language.id), language.name);
    }
    return map_1;
  }, [languagesQuery.languages]);

  // Prefer the LIVE filter state (set by the AI flow and the location filter:
  // `nearLabel` + coords + `radius`) over the legacy region city-search cache —
  // an AI "in Oudenaarde" search writes the former, never the latter, so without
  // this the location chip never appeared (FUN-4558).
  const city = filters.value.nearCleared ? null : isNearPointActive(filters.value) && filters.value.nearLabel != null ? {
    address: filters.value.nearLabel,
    radiusKm: filters.value.radius ?? DEFAULT_RADIUS_KM
  } : cityCache.isMounted && cityCache.state ? {
    address: cityCache.state.address,
    radiusKm: cityCache.state.radiusKm
  } : null;
  return useMemo(() => buildAiFilterChips({
    i18n,
    filters: filters.value,
    regionNameById,
    categoryNameById,
    languageNameById,
    city
  }), [i18n, filters.value, regionNameById, categoryNameById, languageNameById, city?.address, city?.radiusKm]);
}